import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/binti/binti-hero.png';
import Binti2 from '../../assets/images/projects/binti/binti2.png';
import Binti3 from '../../assets/images/projects/binti/binti3.png';
import Binti4 from '../../assets/images/projects/binti/binti4.png';
import Binti5 from '../../assets/images/projects/binti/binti5.png';
import Binti6 from '../../assets/images/projects/binti/binti6.png';
import Binti8 from '../../assets/images/projects/binti/binti8.png';
import Binti9 from '../../assets/images/projects/binti/binti9.png';
import Binti11 from '../../assets/images/projects/binti/binti11.png';
import Binti12 from '../../assets/images/projects/binti/binti12.png';
import Binti13 from '../../assets/images/projects/binti/binti13.png';
import Binti15 from '../../assets/images/projects/binti/binti15.png';
import Binti16 from '../../assets/images/projects/binti/binti16.png';

const Binti = () => {
  return (
    <CaseStudy
      className="binti"
      accent="var(--binti)"
      eyebrow="Design · Figma prototype"
      title="Binti Biz"
      summary="A Figma UX/UI case study: end-to-end screens for a learning platform aimed at women entrepreneurs in Africa, designed with Bloomington studio 3labs."
      links={[
        {
          href: '#frames',
          label: 'View frames'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Binti Biz landing page desktop and mobile composition',
        caption: 'Binti Biz · landing + mobile'
      }}
      date="January 2024"
      role="UX/UI designer — product flows and visual system"
      stack={['Figma', 'Photoshop']}
      brief={{
        label: 'Background',
        they: {
          title: 'The concept',
          body: 'Binti Biz was conceived as an online learning platform for women building businesses in Africa: courses, mentoring, and coaching in one place. This page is the design work for that product concept, not a live build.'
        },
        me: {
          title: 'My role',
          body: (
            <>
              Bloomington design studio{' '}
              <a href="https://www.3labs.us/" target="_blank" rel="noreferrer">
                3labs
              </a>{' '}
              brought me in for UX/UI. Unlike most of my portfolio, this was pure product design in Figma: flows, screens, and a visual system from landing through lessons and community.
            </>
          )
        }
      }}
      problem={{
        title: 'Design the whole product before a single line of code.',
        lead: 'The brief was a serious learning experience for real entrepreneurs, not a shallow “girl boss” skin on a generic course app.',
        detail:
          'I mapped clear paths into the right course, progress that worked on a phone, and community tools that felt useful. Visually, color and energy (including pink) were intentional, without tipping into stereotype. The goal was a complete design story the team could evaluate.'
      }}
      metrics={[
        { value: 'Figma', label: 'Design-only case study' },
        { value: 'Full app', label: 'Flows, not just a homepage' },
        { value: 'Mobile', label: 'Screens designed phone-first' },
        { value: 'Quiz', label: 'Placement into the right course' }
      ]}
      outcome="A full UX/UI prototype in Figma covering signup, placement, learning, community, and progress — an example of my design work beyond engineering."
      solutionsLabel="What I designed"
      solutions={[
        {
          title: 'Landing & signup',
          lead: 'A clear front door, then an account flow that doesn’t feel like homework.',
          detail:
            'The landing page sells the program in plain language. Create-account screens stay short on mobile so getting into the product doesn’t compete with the learning itself.',
          images: [
            {
              src: Binti6,
              alt: 'Binti Biz landing page',
              caption: 'Marketing landing page.'
            },
            {
              src: Binti16,
              alt: 'Landing page mobile',
              caption: 'Landing page on mobile.'
            },
            {
              src: Binti5,
              alt: 'Create account screen',
              caption: 'Create account.'
            },
            {
              src: Binti15,
              alt: 'Create account mobile',
              caption: 'Signup on a phone.'
            }
          ]
        },
        {
          title: 'Placement & home base',
          lead: 'A short quiz routes people into the right course, then a dashboard keeps progress visible.',
          detail:
            'Not every founder starts in the same place. The placement quiz asks enough to assign a track. Once logged in, the dashboard is the home base for courses, status, and next steps.',
          images: [
            {
              src: Binti4,
              alt: 'Placement quiz',
              caption: 'Placement quiz into the right course.'
            },
            {
              src: Binti11,
              alt: 'Logged-in dashboard',
              caption: 'Logged-in dashboard.'
            },
            {
              src: Binti13,
              alt: 'Mobile course dashboard',
              caption: 'Course dashboard on mobile.'
            }
          ]
        },
        {
          title: 'Lessons, resources, community',
          lead: 'The day-to-day product: watch, read, and talk without getting lost.',
          detail:
            'Lesson lists, video viewers, resource libraries, and community spaces were designed as one system. Mobile lesson viewing mattered as much as desktop, because that’s where a lot of learning actually happens.',
          images: [
            {
              src: Binti3,
              alt: 'Course lessons list',
              caption: 'Course lessons list.'
            },
            {
              src: Binti2,
              alt: 'Course lesson detail',
              caption: 'Lesson detail / popup.'
            },
            {
              src: Binti12,
              alt: 'Mobile video lesson viewer',
              caption: 'Video lesson on mobile.'
            },
            {
              src: Binti9,
              alt: 'Course resources page',
              caption: 'Course resources.'
            },
            {
              src: Binti8,
              alt: 'Community page',
              caption: 'Community space for a course.'
            }
          ]
        }
      ]}
      decisions={[
        {
          title: 'Design the whole loop',
          body: 'Landing through lesson playback had to feel like one product. A pretty homepage with “app screens TBD” would not show real UX thinking.'
        },
        {
          title: 'Place people before dumping content',
          body: 'A placement quiz keeps founders from landing in the wrong course. Personalization starts before the first lesson.'
        },
        {
          title: 'Phone-first learning',
          body: 'Dashboards, lessons, and video viewers were designed for small screens first, where a lot of learning actually happens.'
        },
        {
          title: 'Feminine without the gimmick',
          body: 'Color and type stay bold on purpose. Confidence and clarity, not a stereotype layered on a generic course app.'
        }
      ]}
      next={{ link: '/projects/electricfox', label: 'Electric Fox' }}
    />
  );
};

export default transition(Binti);
